import React, { useState } from 'react';
import { Lead } from '../../types/lead';
import { PhoneIcon, CheckIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { followUpState } from './LeadKanban';

/**
 * „Mai követések” sáv: a lejárt és mai követésű leadek egy helyen,
 * egy kattintással lezárható vagy átütemezhető.
 */
interface Props {
  leads: Lead[];
  onDone: (lead: Lead, nextFollowUpAt: Date | null) => Promise<void> | void;
  onOpen: (lead: Lead) => void;
}

const addDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  d.setHours(9, 0, 0, 0);
  return d;
};

const LeadFollowUpBar: React.FC<Props> = ({ leads, onDone, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const end = new Date(); end.setHours(23, 59, 59, 999);
  const due = leads
    .filter(l => l.status !== 'converted' && l.status !== 'unqualified' && l.nextFollowUpAt && l.nextFollowUpAt <= end)
    .sort((a, b) => a.nextFollowUpAt!.getTime() - b.nextFollowUpAt!.getTime());

  if (due.length === 0) return null;

  const overdue = due.filter(l => followUpState(l.nextFollowUpAt).tone === 'late').length;
  const shown = expanded ? due : due.slice(0, 4);

  const act = async (lead: Lead, next: Date | null) => {
    setBusy(lead.id);
    try { await onDone(lead, next); } finally { setBusy(null); }
  };

  return (
    <div className="crm-card overflow-hidden border-amber-200 dark:border-amber-500/30">
      <header className="flex items-center gap-2 px-4 py-2.5 bg-amber-50/70 dark:bg-amber-500/5 border-b border-amber-200/70 dark:border-amber-500/20">
        <ClockIcon className="w-4 h-4 text-amber-700 dark:text-amber-400" />
        <h2 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Mai követések</h2>
        <span className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">
          {due.length} lead{overdue > 0 && <> · <span className="text-red-700 dark:text-red-400 font-medium">{overdue} lejárt</span></>}
        </span>
        {due.length > 4 && (
          <button onClick={() => setExpanded(v => !v)} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            {expanded ? <>Kevesebb <ChevronUpIcon className="w-3.5 h-3.5" /></> : <>Mind a {due.length} <ChevronDownIcon className="w-3.5 h-3.5" /></>}
          </button>
        )}
      </header>
      <ul className="divide-y divide-[var(--crm-border)]">
        {shown.map(lead => {
          const fu = followUpState(lead.nextFollowUpAt);
          const isBusy = busy === lead.id;
          return (
            <li key={lead.id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1.5 px-4 py-2">
              <button onClick={() => onOpen(lead)} className="min-w-0 flex-1 text-left">
                <span className="block text-[13.5px] font-medium text-gray-900 dark:text-gray-50 truncate hover:underline">{lead.name}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{lead.company || lead.email || ''}</span>
              </button>
              <span className={`text-xs tabular-nums flex-shrink-0 ${fu.tone === 'late' ? 'text-red-700 dark:text-red-400 font-medium' : 'text-amber-700 dark:text-amber-400'}`}>
                {fu.text}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0 w-full sm:w-auto">
                {lead.phone && (
                  <a href={`tel:${lead.phone}`} className="crm-btn crm-btn-secondary h-8 px-2.5 text-xs" title={lead.phone}>
                    <PhoneIcon className="w-3.5 h-3.5" /><span className="hidden md:inline">Hívás</span>
                  </a>
                )}
                <button disabled={isBusy} onClick={() => act(lead, addDays(1))} className="crm-btn crm-btn-secondary h-8 px-2.5 text-xs" title="Átütemezés holnapra">
                  +1 nap
                </button>
                <button disabled={isBusy} onClick={() => act(lead, addDays(7))} className="crm-btn crm-btn-secondary h-8 px-2.5 text-xs" title="Átütemezés egy héttel">
                  +1 hét
                </button>
                <button disabled={isBusy} onClick={() => act(lead, null)} className="crm-btn crm-btn-primary h-8 px-2.5 text-xs" title="Kapcsolatfelvétel rögzítése, követés lezárva">
                  <CheckIcon className="w-3.5 h-3.5" />Kész
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LeadFollowUpBar;
